import _ from "lodash";
import { axiosInstance } from "./globals";
import * as userService from "./userService";

import {DataSourceRequest} from "@/model/DataSourceRequest";
import {DataSourceResult} from "@/model/DataSourceResult";
import {MeetingFilter} from "@/model/filters";


export async function createMeeting(data) {
  const headers = userService.getAuthHeaders();
  return (await axiosInstance.post("/Meeting/AddNewMeeting", data, { headers })).data;
}


export async function getAllMeetings(filter: MeetingFilter): Promise<DataSourceResult<any>> {
  const headers = userService.getAuthHeaders();
  const params = {
    fromDate: filter.fromDate,
    toDate: filter.toDate
  };
  const body: DataSourceRequest = {
    page: filter.page,
    pageSize: filter.pageSize,
    filters: [],
    sorts: [
      { field: "CreatedAt", dir: "desc" }
    ]
  };

  if (filter.q) {
    body.filters.push({
      field: "name", operator: "contains", value: filter.q, logic: "and"
    });
  }

  if (filter.status) {
    body.filters.push({
      field: "status", operator: "eq", value: filter.status, logic: "and"
    });
  }
  
  return (await axiosInstance.post<DataSourceResult<any>>("/Meeting/GetMeetingList", body, { headers, params })).data;
}


export async function getAllMeetingsByCustomer(customerId: number | string, filter: MeetingFilter): Promise<DataSourceResult<any>> {
  const headers = userService.getAuthHeaders();

  const body: DataSourceRequest = {
    page: filter.page,
    pageSize: filter.pageSize,
    filters: [],
    sorts: [
      { field: "id", dir: "desc" }
    ]
  };

  if (filter.q) {
    body.filters.push({
      field: "name", operator: "contains", value: filter.q, logic: "and"
    });
  }

  if (filter.status) {
    body.filters.push({
      field: "status", operator: "eq", value: filter.status, logic: "and"
    });
  }

  return (await axiosInstance.post<DataSourceResult<any>>(`/Meeting/GetMeetingListByCustomer/${customerId}`, body, { headers })).data;
}


export async function getDetails(id: number) {
  const headers = userService.getAuthHeaders();
  const meeting = (await axiosInstance.get(`/Meeting/GetMeetingDetail/${id}`, { headers })).data;

  if (meeting.status === "INPROGRESS") {
    meeting.participantsJoined = (await axiosInstance.get(`/Meeting/ParticipantsJoined/${id}`, { headers })).data;

    for (const p of meeting.participantsJoined) {
      const matchParticipantByName = meeting.meetingParticipants.find(it => it.name === p.name);
      if (matchParticipantByName) {
        p.email = matchParticipantByName.email;
        p.phone = matchParticipantByName.phone;
      }
    }

    meeting.participantsNotJoined = meeting.meetingParticipants.filter(m => meeting.participantsJoined.every(pj => pj.name !== m.name));
  }

  return meeting;
}


export async function getStoragePlan() {
  const headers = userService.getAuthHeaders();
  return (await axiosInstance.get("/Meeting/StoragePlan", { headers })).data;
}


export async function updateMeeting(data: any) {
  const headers = userService.getAuthHeaders();
  return (await axiosInstance.post("/Meeting/UpdateMeeting", data, { headers })).data;
}


export async function cancelMeeting(meetingId: number) {
  const headers = userService.getAuthHeaders();
  const params = { meetingId };
  return (await axiosInstance.post("/Meeting/CancelMeeting", null, { headers, params })).data;
}


export async function deleteMeeting(id: number) {
  const headers = userService.getAuthHeaders();
  const params = { id };
  return (await axiosInstance.post("/Meeting/DeleteMeeting", null, { headers, params })).data;
}


export async function startMeeting(meetingId: number) {
  const headers = userService.getAuthHeaders();
  const params = { meetingId };
  return (await axiosInstance.post("/Meeting/StartMeeting", null, { headers, params })).data;
}


export async function joinMeeting(meetingId: number) {
  const headers = userService.getAuthHeaders();
  const params = { meetingId };
  return (await axiosInstance.post("/Meeting/JoinMeeting", null, { headers, params })).data;
}


export async function endMeeting(meetingId: number) {
  const headers = userService.getAuthHeaders();
  const params = { meetingId };
  return (await axiosInstance.post("/Meeting/EndMeeting", null, { headers, params })).data;
}

export async function startNewMeeting() {
  const headers = userService.getAuthHeaders();
  return (await axiosInstance.post("/Meeting/StartNewMeeting", null, { headers })).data;
}


export async function sendInvitationEmails(meetingId: number) {
  const headers = userService.getAuthHeaders();
  const params = { meetingId };

  return (await axiosInstance.post("/Meeting/SendInvitation", null, { headers, params })).data;
}


export async function sendNotificationEmails(meetingId: number, status: string) {
  const headers = userService.getAuthHeaders();
  const params = { meetingId, status };

  return (await axiosInstance.post("/Meeting/SendEmailNotification", null, { headers, params })).data;
}


export async function sendSingleInvitationEmail(meetingId: number, participantId: number) {
  const headers = userService.getAuthHeaders();
  const params = { meetingId, participantId };

  return (await axiosInstance.post("/Meeting/SendSingleInvitation", null, { headers, params })).data;
}


export async function sendInvitationSms(meetingId: number, participantId: number) {
  const headers = userService.getAuthHeaders();
  const params = { meetingId, participantId };

  return (await axiosInstance.post("/Meeting/SendSmsSingleInvitation", null, { headers, params })).data;
}


export async function addNewParticipant(data) {
  const headers = userService.getAuthHeaders();
  return (await axiosInstance.post("/MeetingParticipants/AddNewParticipant", data, { headers })).data;
}

export async function updateParticipant(data) {
  const headers = userService.getAuthHeaders();
  return (await axiosInstance.post("/MeetingParticipants/UpdateParticipant", data, { headers })).data;
}


export async function getBySecretKey(meetingSecretKey: string) {
  const params = { meetingSecretKey };

  return (await axiosInstance.get("/JoinMeetings/GetMeetingInfo", { params })).data;
}


export async function joinBySecretKey(params: { meetingSecretKey: string, accessCode: string, org?: string }) {
  return (await axiosInstance.post("/JoinMeetings/Join", null, { params })).data;
}

export async function joinBySecretKeyAndName(params: { meetingSecretKey: string, accessCode: string, fullName: string, org?: string }) {
  return (await axiosInstance.post("/JoinMeetings/JoinWithName", null, { params })).data;
}


export async function getMeetingRecording(meetingId: number) {
  const headers = userService.getAuthHeaders();
  const params = { meetingId };

  return (await axiosInstance.post("/Meeting/MeetingRecording", null, { headers, params })).data;
}


export async function getPreviewMeetingEmail(meetingId: number, status: string) {
  const headers = userService.getAuthHeaders();
  const params = { meetingId, status };

  return (await axiosInstance.get("/Meeting/PreviewMeetingEmail", { headers, params })).data;
}


export async function getParticipantMeeting(params) {
  const headers = userService.getAuthHeaders();
  return (await axiosInstance.get("/ThongKe/GetParticipantMeeting", { headers, params })).data.data;
}

export async function getContentMeeting(params) {
  const headers = userService.getAuthHeaders();
  return (await axiosInstance.get("/ThongKe/GetContentMeeting", { headers, params })).data.data;
}